import React, { useEffect, useRef, useState } from 'react';
import { Alert, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, LinearProgress, MenuItem, TextField } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import CrudService from 'services/cruds-service';
import { adaptContactPrefill } from './contactPrefillAdapter';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
function uploadKind(file) {
  if (IMAGE_TYPES.includes(file.type)) return 'image';
  if (/\.txt$/i.test(file.name) && ['', 'text/plain'].includes(file.type)) return 'text';
  return '';
}
function inputError(files) {
  const images = files.filter((file) => uploadKind(file) === 'image');
  const texts = files.filter((file) => uploadKind(file) === 'text');
  if (files.some((file) => !uploadKind(file))) return 'Choose TXT, JPEG, PNG, or WebP files.';
  if (images.length > 5 || texts.length > 5) return 'Choose at most five images and five text files.';
  if (images.some((file) => file.size > 10 * 1024 * 1024) ||
      images.reduce((total, file) => total + file.size, 0) > 20 * 1024 * 1024) {
    return 'Images must be at most 10 MB each and 20 MB combined.';
  }
  if (texts.some((file) => file.size > 100 * 1024)) return 'Text files must be at most 100 KB each.';
  return '';
}

export default function ContactPrefillDialog({ onClose, onApply }) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [country, setCountry] = useState('US');
  const [draft, setDraft] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  useEffect(() => () => { sequence.current += 1; }, []);

  const generate = async () => {
    const current = ++sequence.current;
    setLoading(true); setError(''); setDraft(null); setSelectedIds([]);
    try {
      const problem = inputError(files);
      if (problem) throw new Error(problem);
      const textFiles = files.filter((file) => uploadKind(file) === 'text');
      const combinedText = [text, ...await Promise.all(textFiles.map((file) => file.text()))].join('\n\n');
      if (combinedText.length > 50000) throw new Error('Use at most 50,000 characters of text.');
      const response = await CrudService.parseContactPrefill({
        text: combinedText, defaultCountry: country,
        files: files.filter((file) => uploadKind(file) === 'image'),
      });
      const next = adaptContactPrefill(response, () => crypto.randomUUID());
      if (current === sequence.current) setDraft(next);
    } catch (failure) {
      if (current === sequence.current) {
        const code = failure?.error || failure?.response?.data?.error;
        const localMessages = ['Use at most 50,000 characters of text.', inputError(files)].filter(Boolean);
        setError(localMessages.includes(failure?.message) ? failure.message :
          code === 'RATE_LIMITED' ? 'Too many requests. Try again shortly.' :
          code === 'FORBIDDEN' ? 'You do not have permission to extract contacts.' :
          'Contact extraction failed. Your input was kept; try again.');
      }
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  };
  const edit = (id, field, value) => setDraft((previous) => ({
    ...previous, candidates: previous.candidates.map((candidate) =>
      candidate.id === id ? { ...candidate, [field]: value } : candidate),
  }));
  const toggle = (id, checked) => setSelectedIds((previous) =>
    checked ? [...previous, id] : previous.filter((selectedId) => selectedId !== id));
  const apply = () => {
    const errors = onApply(draft.candidates, selectedIds);
    if (errors.length) { setError(errors.map((issue) => issue.reason).join('; ')); return; }
    onClose();
  };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contact AI Prefill</DialogTitle>
      {loading && <LinearProgress />}
      <DialogContent dividers>
        <Alert severity="info">Contact notes and images are sent to Rivio&apos;s AI service. No contacts or invitations are created here.</Alert>
        <TextField fullWidth multiline minRows={5} label="Contact notes" value={text}
          disabled={loading} onChange={(event) => { setText(event.target.value); setDraft(null); setSelectedIds([]); }} sx={{ mt: 2 }} />
        <TextField select label="Phone country" value={country} disabled={loading} sx={{ mt: 2, minWidth: 180, '& .MuiOutlinedInput-root': { height: '2.2rem' } }}
          onChange={(event) => { setCountry(event.target.value); setDraft(null); setSelectedIds([]); }}>
          <MenuItem value="US">United States</MenuItem>
          <MenuItem value="CA">Canada</MenuItem>
          <MenuItem value="GB">United Kingdom</MenuItem>
        </TextField>
        <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
          <MDButton component="label" variant="outlined" color="info" disabled={loading}>
            Add files
            <input hidden multiple type="file" accept=".txt,image/jpeg,image/png,image/webp"
              onChange={(event) => {
                const next = [...files, ...Array.from(event.target.files || [])];
                event.target.value = '';
                const problem = inputError(next);
                setError(problem);
                if (!problem) { setFiles(next); setDraft(null); setSelectedIds([]); }
              }} />
          </MDButton>
          {files.map((file, index) => <Chip key={`${file.name}-${index}`} label={file.name}
            onDelete={loading ? undefined : () => {
              setFiles((previous) => previous.filter((_, fileIndex) => index !== fileIndex));
              setDraft(null); setSelectedIds([]);
            }} />)}
        </MDBox>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {draft?.warnings.map((warning, index) => <Alert severity="warning" key={index} sx={{ mt: 2 }}>{warning}</Alert>)}
        {draft && draft.candidates.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>No contact details found.</Alert>}
        {draft?.candidates.map((candidate, index) => (
          <MDBox key={candidate.id} mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <FormControlLabel label={`Include contact ${index + 1}`} control={<Checkbox
              checked={selectedIds.includes(candidate.id)} onChange={(event) => toggle(candidate.id, event.target.checked)} />} />
            <MDBox sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {[['contact_first_name', 'First name'], ['contact_last_name', 'Last name'], ['email', 'Email'], ['phone', 'Phone (+country code)']]
                .map(([field, label]) => <TextField key={field} fullWidth label={label} value={candidate[field]}
                  onChange={(event) => edit(candidate.id, field, event.target.value)} />)}
            </MDBox>
            {candidate.inferredFields.length > 0 && <Alert severity="warning" sx={{ mt: 1 }}>Confirm inferred fields: {candidate.inferredFields.join(', ')}</Alert>}
            {candidate.warnings.map((warning, warningIndex) => <Alert severity="warning" key={warningIndex} sx={{ mt: 1 }}>{candidate.warnings[warningIndex] || warning}</Alert>)}
            <MDTypography variant="caption" display="block" sx={{ overflowWrap: 'anywhere', mt: 1 }}>{candidate.excerpt}</MDTypography>
          </MDBox>
        ))}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton color="info" startIcon={<AutoAwesomeOutlinedIcon />} onClick={generate}
          disabled={loading || (!text.trim() && files.length === 0)}>Generate draft</MDButton>
        <MDButton color="success" onClick={apply} disabled={loading || !draft || selectedIds.length === 0}>Apply selected</MDButton>
      </DialogActions>
    </Dialog>
  );
}
